import { ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import {
  getCurrentUser,
  selectIsInUnservicedFreeTrial,
} from '@/selectors/user.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useIsDeviceWidth, DeviceWidth } from '@/utils/deviceWidthHelpers'
import { fetchUserCatchUpBookkeepingStatus } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import { selectOnboardingStepCompletedOrSkipped } from '@/features/onboarding/user-onboarding-steps/onboarding.selectors'
import { useSelectUserShouldSeeOnboardingDashboard } from '@/features/onboarding/user-onboarding-steps/onboarding.hooks'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'

import { FreeTrialGettingStarted } from './free-trial-onboarding/FreeTrialGettingStarted'
import { RecommendedForYou } from './free-trial-onboarding/RecommendedForYou'
import { SupportForYou } from './free-trial-onboarding/SupportForYou'
import { OnboardingDashboardSurvey } from './OnboardingDashboardSurvey'
import { CubkCard } from './catch-up-bookkeeping/CubkCard'
import { selectCubkDisplayPhase } from './catch-up-bookkeeping/cubk.selectors'
import { CubkCardState } from './catch-up-bookkeeping/lib/cubkProgress.types'
import { OnboardingItemList } from './getting-started-items/OnboardingItemList'

import { PracticeOverviewCard } from '@/features/user/dashboard/cards/PracticeOverviewCard'
import { GettingStartedOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { MaximizeTaxDeductionsCard } from '@/features/user/dashboard/insights-cards/MaximizeTaxDeductionsCard'
import { Card, CardColors, Loader, Text } from '@/ui'
import { GowSignupPanel } from './GowSignupPanel'
import { TypographyStyles } from '@/styles'
import { fetchCurrentUserDetailsIfNeeded } from '@/actions/userActions'
import { fetchBankingProfile } from '@/features/banking/bankingSlice'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'

const OnboardingDashboardWrapper = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const isTablet = useIsDeviceWidth(DeviceWidth.tablet)

  return (
    <div className={`w-full ${className}`}>
      <Card
        color={CardColors.GREEN}
        className={`
            ${isMobile ? 'mt-4 py-8 px-4' : isTablet ? 'mt-4 py-16 px-6' : 'py-16 px-10'}
            getting-started
          `}
      >
        <FreeTrialGettingStarted />
      </Card>
      <div
        className={`
            ${isMobile ? 'pt-10 px-4 pb-6' : isTablet ? 'p-10' : 'py-14 px-10'}
            additional-onboarding-content
          `}
      >
        {children}
      </div>
    </div>
  )
}

export const OnboardingDashboard = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const userShouldSeeOnboardingDashboard =
    useSelectUserShouldSeeOnboardingDashboard()
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)

  const hasConnectedBankAccount = useReselector(
    selectOnboardingStepCompletedOrSkipped,
    GettingStartedOnboardingStep.connectBusinessBankAccount
  )

  const showObSurvey = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.showOnboardingSurveys,
    false
  )

  const currentUser = useReselector(getCurrentUser)
  const cubkDisplayPhase = useReselector(selectCubkDisplayPhase)
  const isCubkComplete = cubkDisplayPhase === CubkCardState.COMPLETE

  useEffect(() => {
    async function batchFetch() {
      await Promise.all([
        fetchUserCatchUpBookkeepingStatus()(dispatch),
        dispatch(fetchCurrentUserDetailsIfNeeded(true)),
        dispatch(fetchBankingProfile()),
        dispatch(fetchFinancialAccountsIfNeeded()),
      ])
      setLoading(false)
    }
    batchFetch()
  }, [dispatch])

  useEffect(() => {
    if (!userShouldSeeOnboardingDashboard) {
      navigate('/dashboard')
    }
  }, [navigate, userShouldSeeOnboardingDashboard])

  // Hold the loader through the redirect — otherwise the dashboard flashes
  // for one paint before the navigate effect fires.
  if (loading || !userShouldSeeOnboardingDashboard) {
    return <Loader active />
  }

  if (isInUnservicedFreeTrial) {
    return (
      <OnboardingDashboardWrapper>
        <RecommendedForYou />
        <br />
        <br />
        <br />
        <SupportForYou />
      </OnboardingDashboardWrapper>
    )
  }

  return (
    <div className="w-full px-4 py-8 sm:px-6 sm:py-10 lg:px-10 lg:py-14">
      {!isCubkComplete && (
        <Text variant={TypographyStyles.DISPLAY_SM} className="mb-6">
          Hi {currentUser?.firstName}! You&apos;re almost set-up.
        </Text>
      )}
      <CubkCard />
      <div className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-stretch">
        <div className="flex-1 min-w-0">
          <OnboardingItemList />
        </div>
        <GowSignupPanel />
      </div>
      {hasConnectedBankAccount && (
        <div className="mt-8">
          <PracticeOverviewCard />
          <MaximizeTaxDeductionsCard />
        </div>
      )}
      {showObSurvey && <OnboardingDashboardSurvey />}
    </div>
  )
}
